import { DataType, FormInputType, IndexType } from '@doss/prisma/types'
import { DossError } from './doss-error.js'
import { FormArrayOption } from './form-block-schemas.js'
import { isDefined } from './type-guards.js'
import { normalizeEnumLabel } from './types/enum-option.js'
import { AllowedArrayInnerTypes } from './types/table-column.js'

/**
 * Result type for form input type conversion
 */
export type FormInputDataTypeConfig = {
  dataType: DataType
  indexDataType: IndexType
  innerDataType: AllowedArrayInnerTypes | null
}

/**
 * Converts a FormInputType and isMultiSelect boolean into the appropriate
 * data type, index type, and inner type combination.
 */
export function getDataModelForFormInputType(
  inputType: FormInputType,
  isMultiSelect: boolean,
): FormInputDataTypeConfig {
  const defaultIndexType = IndexType.NONE

  const multiSelectConfigurableInputTypes = [
    FormInputType.DROPDOWN.toString(),
    FormInputType.FILE.toString(),
    FormInputType.LOOKUP.toString(),
    FormInputType.USER.toString(),
  ] as const

  if (isMultiSelect && !multiSelectConfigurableInputTypes.includes(inputType.toString())) {
    throw new DossError('Multi-select is not supported for input type %s', [inputType])
  }

  switch (inputType) {
    case FormInputType.BOOLEAN:
      return {
        dataType: DataType.BOOLEAN,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.CHECKBOXES:
      return {
        dataType: DataType.ARRAY,
        indexDataType: defaultIndexType,
        innerDataType: DataType.ENUM,
      }

    case FormInputType.DATE:
      return {
        dataType: DataType.DATE,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.DATE_TIME:
      return {
        dataType: DataType.DATE_TIME,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.DROPDOWN:
      if (isMultiSelect) {
        return {
          dataType: DataType.ARRAY,
          indexDataType: defaultIndexType,
          innerDataType: DataType.ENUM,
        }
      }
      return {
        dataType: DataType.ENUM,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.EMAIL:
      return {
        dataType: DataType.STRING,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.FILE:
      if (isMultiSelect) {
        return {
          dataType: DataType.ARRAY,
          indexDataType: defaultIndexType,
          innerDataType: DataType.FILE_URL,
        }
      }
      return {
        dataType: DataType.FILE_URL,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.FORMULA:
      return {
        dataType: DataType.FORMULA_NUMBER,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.LONG_TEXT:
      return {
        dataType: DataType.STRING,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.LOOKUP:
      if (isMultiSelect) {
        return {
          dataType: DataType.ARRAY,
          indexDataType: defaultIndexType,
          innerDataType: DataType.LOOKUP,
        }
      }
      return {
        dataType: DataType.LOOKUP,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.NUMBER:
      return {
        dataType: DataType.NUMBER,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.PHONE_NUMBER:
      return {
        dataType: DataType.STRING,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.RADIO_BUTTON:
      return {
        dataType: DataType.ENUM,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.SHORT_TEXT:
      return {
        dataType: DataType.STRING,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.SIGNATURE:
      return {
        dataType: DataType.FILE_URL,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.URL:
      return {
        dataType: DataType.STRING,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }

    case FormInputType.USER:
      if (isMultiSelect) {
        return {
          dataType: DataType.ARRAY,
          indexDataType: defaultIndexType,
          innerDataType: DataType.USER,
        }
      }
      return {
        dataType: DataType.USER,
        indexDataType: defaultIndexType,
        innerDataType: null,
      }
  }
}

/**
 * Determines if a form input should be multi-select based on its data type configuration and input type.
 */
export function getViewModelForFormInputType(
  inputType: FormInputType,
  dataTypeConfig: FormInputDataTypeConfig,
): boolean {
  const { dataType, innerDataType } = dataTypeConfig

  switch (dataType) {
    case DataType.ARRAY:
      if (innerDataType === null) {
        throw new DossError('Array data type must have an inner data type', [])
      }
      switch (innerDataType) {
        case DataType.ENUM:
          if (inputType === FormInputType.CHECKBOXES) {
            return false // CHECKBOXES are inherently multi-select, so flag is not needed on the view model
          }
          if (inputType === FormInputType.DROPDOWN) {
            return true
          }
          throw new DossError(
            'ARRAY + ENUM data type requires CHECKBOXES or DROPDOWN input type, got %s',
            [String(inputType)],
          )

        case DataType.FILE_URL:
          if (inputType === FormInputType.FILE) {
            return true
          }
          throw new DossError('ARRAY + FILE_URL data type requires FILE input type, got %s', [
            String(inputType),
          ])

        case DataType.LOOKUP:
          if (inputType === FormInputType.LOOKUP) {
            return true
          }
          throw new DossError('ARRAY + LOOKUP data type requires LOOKUP input type, got %s', [
            String(inputType),
          ])

        case DataType.USER:
          if (inputType === FormInputType.USER) {
            return true
          }
          throw new DossError('ARRAY + USER data type requires USER input type, got %s', [
            String(inputType),
          ])
      }
      break
    case DataType.BOOLEAN:
      if (inputType === FormInputType.BOOLEAN) {
        return false
      }
      throw new DossError('BOOLEAN data type requires BOOLEAN input type, got %s', [
        String(inputType),
      ])

    case DataType.DATE:
      if (inputType === FormInputType.DATE) {
        return false
      }
      throw new DossError('DATE data type requires DATE input type, got %s', [String(inputType)])

    case DataType.DATE_TIME:
      if (inputType === FormInputType.DATE_TIME) {
        return false
      }
      throw new DossError('DATE_TIME data type requires DATE_TIME input type, got %s', [
        String(inputType),
      ])

    case DataType.ENUM:
      if (inputType === FormInputType.DROPDOWN || inputType === FormInputType.RADIO_BUTTON) {
        return false
      }
      throw new DossError('ENUM data type requires DROPDOWN or RADIO_BUTTON input type, got %s', [
        String(inputType),
      ])

    case DataType.FILE_URL:
      if (inputType === FormInputType.FILE || inputType === FormInputType.SIGNATURE) {
        return false
      }
      throw new DossError('FILE_URL data type requires FILE or SIGNATURE input type, got %s', [
        String(inputType),
      ])

    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_BOOLEAN:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return false

    case DataType.LOOKUP:
      if (inputType === FormInputType.LOOKUP) {
        return false
      }
      throw new DossError('LOOKUP data type requires LOOKUP input type, got %s', [
        String(inputType),
      ])

    case DataType.NUMBER:
      if (inputType === FormInputType.NUMBER) {
        return false
      }
      throw new DossError('NUMBER data type requires NUMBER input type, got %s', [
        String(inputType),
      ])

    case DataType.STRING:
      if (
        inputType === FormInputType.SHORT_TEXT ||
        inputType === FormInputType.LONG_TEXT ||
        inputType === FormInputType.EMAIL ||
        inputType === FormInputType.PHONE_NUMBER ||
        inputType === FormInputType.URL
      ) {
        return false
      }
      throw new DossError(
        'STRING data type requires SHORT_TEXT, LONG_TEXT, EMAIL, PHONE_NUMBER, or URL input type, got %s',
        [String(inputType)],
      )

    case DataType.USER:
      if (inputType === FormInputType.USER) {
        return false
      }
      throw new DossError('USER data type requires USER input type, got %s', [String(inputType)])

    case DataType.ID:
      throw new DossError('ID data type is not supported for form inputs, got %s', [
        String(inputType),
      ])
  }
}

/**
 * Returns an array of valid FormInputType values for a given DataType/innerDataType combination.
 * Used for validation and UI dropdowns when linking form inputs to table columns.
 */
export function getValidFormInputTypesForDataType(
  dataType: DataType,
  innerDataType: DataType | null = null,
): FormInputType[] {
  // Handle array types
  if (dataType === DataType.ARRAY) {
    if (innerDataType === null) {
      throw new DossError('Array data type must have an inner data type', [])
    }
    switch (innerDataType) {
      case DataType.ENUM:
        return [FormInputType.CHECKBOXES, FormInputType.DROPDOWN]
      case DataType.FILE_URL:
        return [FormInputType.FILE]
      case DataType.LOOKUP:
        return [FormInputType.LOOKUP]
      case DataType.USER:
        return [FormInputType.USER]
      case DataType.BOOLEAN:
      case DataType.DATE:
      case DataType.DATE_TIME:
      case DataType.NUMBER:
      case DataType.ARRAY:
      case DataType.ID:
      case DataType.STRING:
        throw new DossError('Unsupported inner data type for ARRAY: %s', [String(innerDataType)])
      case DataType.FORMULA_NUMBER:
      case DataType.FORMULA_STRING:
      case DataType.FORMULA_BOOLEAN:
      case DataType.FORMULA_DATE:
      case DataType.FORMULA_DATE_TIME:
        throw new DossError('Unsupported inner data type for ARRAY: %s', [String(innerDataType)])
    }
  }

  // Handle non-array types
  switch (dataType) {
    case DataType.BOOLEAN:
      return [FormInputType.BOOLEAN]
    case DataType.DATE:
      return [FormInputType.DATE]
    case DataType.DATE_TIME:
      return [FormInputType.DATE_TIME]
    case DataType.ENUM:
      return [FormInputType.DROPDOWN, FormInputType.RADIO_BUTTON]
    case DataType.FILE_URL:
      return [FormInputType.FILE, FormInputType.SIGNATURE]
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_BOOLEAN:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return [FormInputType.FORMULA]
    case DataType.LOOKUP:
      return [FormInputType.LOOKUP]
    case DataType.NUMBER:
      return [FormInputType.NUMBER]
    case DataType.STRING:
      return [
        FormInputType.SHORT_TEXT,
        FormInputType.LONG_TEXT,
        FormInputType.EMAIL,
        FormInputType.PHONE_NUMBER,
        FormInputType.URL,
      ]
    case DataType.USER:
      return [FormInputType.USER]
    case DataType.ID:
      throw new DossError('ID data type is not supported for form inputs', [])
  }
}

/**
 * Returns the default/preferred FormInputType for a given DataType/innerDataType combination.
 * Used when creating new form inputs linked to table columns.
 */
export function getDefaultFormInputTypeForDataType(
  dataType: DataType,
  innerDataType: DataType | null = null,
): FormInputType {
  // Handle array types
  if (dataType === DataType.ARRAY) {
    if (innerDataType === null) {
      throw new DossError('Array data type must have an inner data type', [])
    }
    switch (innerDataType) {
      case DataType.ENUM:
        return FormInputType.DROPDOWN // Prefer DROPDOWN over CHECKBOXES for multi-select
      case DataType.FILE_URL:
        return FormInputType.FILE
      case DataType.LOOKUP:
        return FormInputType.LOOKUP
      case DataType.USER:
        return FormInputType.USER
      case DataType.BOOLEAN:
      case DataType.DATE:
      case DataType.DATE_TIME:
      case DataType.FORMULA_NUMBER:
      case DataType.FORMULA_STRING:
      case DataType.FORMULA_BOOLEAN:
      case DataType.FORMULA_DATE:
      case DataType.FORMULA_DATE_TIME:
      case DataType.NUMBER:
      case DataType.ARRAY:
      case DataType.ID:
      case DataType.STRING:
        throw new DossError('Unsupported inner data type for ARRAY: %s', [String(innerDataType)])
    }
  }

  // Handle non-array types
  switch (dataType) {
    case DataType.BOOLEAN:
      return FormInputType.BOOLEAN
    case DataType.DATE:
      return FormInputType.DATE
    case DataType.DATE_TIME:
      return FormInputType.DATE_TIME
    case DataType.ENUM:
      return FormInputType.DROPDOWN // Prefer DROPDOWN over RADIO_BUTTON
    case DataType.FILE_URL:
      return FormInputType.FILE // Prefer FILE over SIGNATURE
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_BOOLEAN:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      return FormInputType.FORMULA
    case DataType.LOOKUP:
      return FormInputType.LOOKUP
    case DataType.NUMBER:
      return FormInputType.NUMBER
    case DataType.STRING:
      return FormInputType.SHORT_TEXT // Default to SHORT_TEXT for strings
    case DataType.USER:
      return FormInputType.USER
    case DataType.ID:
      throw new DossError('ID data type is not supported for form inputs', [])
  }
}

/**
 * Validates whether a FormInputType is compatible with a given DataType/innerDataType combination.
 * Returns true if valid, false otherwise.
 */
export function validateFormInputTypeForDataType(
  inputType: FormInputType,
  dataType: DataType,
  innerDataType: DataType | null = null,
): boolean {
  const validTypes = getValidFormInputTypesForDataType(dataType, innerDataType)
  return validTypes.includes(inputType)
}

/**
 * Whether a resolved form input value counts as "empty" for required-field
 * validation: null/undefined, an empty array, or an array whose elements are
 * all null/undefined. `false` and `0` are not empty.
 */
export const isEffectivelyEmptyFormInputValue = (value: unknown): boolean => {
  if (!isDefined(value)) {
    return true
  }
  if (Array.isArray(value)) {
    return value.length === 0 || value.every((e) => !isDefined(e))
  }
  return false
}

// Generate stable value from label: "Green Apple!" → "green_apple"
function generateValueFromLabel(label: string): string {
  return (
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || 'option'
  )
}

// Calculate unique value by checking existing options
// Returns the unique value without mutating anything
export function generateUniqueFormOptionValueFromLabel(
  label: string,
  existingOptions: FormArrayOption[],
  currentIndex?: number,
): string {
  const baseValue = generateValueFromLabel(label)

  const otherValues = existingOptions
    .map((opt, idx) => ({ value: opt.value, idx }))
    .filter(({ idx }) => idx !== currentIndex)
    .map(({ value }) => value)

  if (!otherValues.includes(baseValue)) {
    return baseValue
  }

  // Find next available suffix: "yes", "yes-2", "yes-3", etc.
  const findUniqueValue = (base: string, counter: number): string => {
    const candidate = `${base}-${counter}`
    return otherValues.includes(candidate) ? findUniqueValue(base, counter + 1) : candidate
  }

  return findUniqueValue(baseValue, 2)
}

/**
 * Returns the labels an input's options use more than once, compared on the trimmed label the way
 * enum option labels are.
 */
export function findDuplicateFormOptionLabels(options: Array<{ label: string }>): string[] {
  const seenLabels = new Set<string>()
  const duplicateLabels = new Set<string>()

  options.forEach(({ label }) => {
    const key = normalizeEnumLabel(label)
    if (seenLabels.has(key)) {
      duplicateLabels.add(key)
    } else {
      seenLabels.add(key)
    }
  })

  return [...duplicateLabels]
}
